import { Injectable } from '@angular/core';
import { State, Action, Selector, StateContext } from '@ngxs/store';
import { HubOverviewInitialState, HubOverviewLoad, HubOverviewUpdate } from './hub-overview.actions';
import { HubOverview } from '../../models';
import { catchError, map, take } from 'rxjs/operators';
import { Observable, of } from 'rxjs';
import { HubOverviewService } from './hub-overview.service';
import { NotificationService } from '../../../../../../common/services/notification/notification.service';
import { Error, CustomHttpErrorResponse } from '../../../../../../common';
import { MatSnackBarRef, SimpleSnackBar } from '@angular/material/snack-bar';

export interface HubOverviewStateModel {
  overview: HubOverview;
  loaded: boolean;
}

const initialState: HubOverviewStateModel = {
  overview: null,
  loaded: false,
};

@State<HubOverviewStateModel>({
  name: 'hubOverview',
  defaults: initialState,
})
@Injectable()
export class HubOverviewState {
  constructor(private hubOverviewService: HubOverviewService, private notificationService: NotificationService) {}

  @Selector()
  static getState(state: HubOverviewStateModel): HubOverviewStateModel {
    return state;
  }

  @Selector()
  static getLoaded(state: HubOverviewStateModel): boolean {
    return state.loaded;
  }

  @Selector()
  static getName(state: HubOverviewStateModel): string {
    return state.overview?.name;
  }

  @Selector()
  static getOverview(state: HubOverviewStateModel): HubOverview {
    return state.overview;
  }

  @Action(HubOverviewLoad)
  getHubOverview(
    ctx: StateContext<HubOverviewStateModel>,
    { payload }: HubOverviewLoad,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubOverviewService.getOverviewData(payload).pipe(
      map((overview: HubOverview) => {
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubOverviewUpdate)
  updateHubOverview(
    ctx: StateContext<HubOverviewStateModel>,
    { payload }: HubOverviewUpdate,
  ): Observable<MatSnackBarRef<SimpleSnackBar> | void> {
    return this.hubOverviewService.updateHubOverview(payload).pipe(
      take(1),
      map((overview: HubOverview) => {
        this.notificationService.success('api.hub.success.update');
        ctx.patchState({ overview, loaded: true });
      }),
      catchError(({ error }: CustomHttpErrorResponse<Error>) => of(this.notificationService.error(error.message))),
    );
  }

  @Action(HubOverviewInitialState)
  setInitialState(ctx: StateContext<HubOverviewStateModel>): void {
    ctx.setState(initialState);
  }
}
